import React from 'react';
import { Badge, FormGroup } from 'reactstrap';
import { toast } from 'react-toastify';
import { faCheckCircle, faPhoneSlash } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    FormLabel,
    onFieldChange,
    onReactSelectChanged,
    ToastMessage,
    FormCheckbox,
    Input,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { JobAssignmentCancellationType } from './JobAssignmentCancellationType';

const getBadgeColor = (value) => (value ? 'success' : 'secondary');
const toYesNo = (value) => (value ? 'Yes' : 'No');

export class JobAssignmentCancellationTypeForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            jobAssignmentCancellationType:
                this.props.jobAssignmentCancellationType
                ?? new JobAssignmentCancellationType(),

            show: false,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    onChange = onFieldChange.bind(this);

    onClose(response) {
        this.setState({ show: false });
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onDelete = async () => {
        const { jobAssignmentCancellationType } = this.state;
        const response = await httpClient
            .delete(
                ApiRoutes.jobAssignmentCancellationType.delete(
                    jobAssignmentCancellationType.id,
                ),
            )
            .catch(this.handleSaveError);

        if (response) {
            this.onClose(response);
        }
    };

    onSelectChange = onReactSelectChanged.bind(this);

    onSubmit = async () => {
        const { jobAssignmentCancellationType } = this.state;

        // Clear any fluent api errors
        this.setState({ errors: {}, saving: true });

        // Is this POST or PUT?
        const url = jobAssignmentCancellationType.id
            ? ApiRoutes.jobAssignmentCancellationType.update()
            : ApiRoutes.jobAssignmentCancellationType.create();

        const response = await httpClient.andGetResponse(
            httpClient.types.post,
            url,
            jobAssignmentCancellationType,
            'Error Saving',
        );

        if (response) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message={`Job Assignment Cancellation Type [${jobAssignmentCancellationType.description}] saved.`}
                />,
            );
            this.setState({ show: false, saving: false });
            this.context.setFormOpened(false);
            this.resetForm();
            this.context.setFormOpened(false);
            this.props.onSaveCallback(response);
        }
    };

    handleSaveError = handleFormSaveError.bind(this);

    open = async (id) => {
        this.resetForm();
        this.setState({ show: true, loading: true });
        this.context.setFormOpened(true);

        const [jobAssignmentCancellationType] = await Promise.all([
            id
                ? httpClient.js(
                    ApiRoutes.jobAssignmentCancellationType.byId(id),
                )
                : new JobAssignmentCancellationType(),
        ]);

        this.setState({
            jobAssignmentCancellationType,
            loading: false,
        });
    };

    resetForm() {
        this.setState({ formValidated: false });
    }

    render() {
        const {
            formValidated,
            validationMessage,
            jobAssignmentCancellationType,
            loading,
            show,
        } = this.state;

        const showDelete = Boolean(
            jobAssignmentCancellationType.id
            && jobAssignmentCancellationType.canBeDeleted
            && !jobAssignmentCancellationType.isDeleted,
        );

        const isAdmin = this.context?.user?.isAdmin;
        const canEdit = jobAssignmentCancellationType.canBeModified || isAdmin;

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="jobAssignmentCancellationTypeForm"
                formIcon={faPhoneSlash}
                formTitle={
                    !!parseInt(jobAssignmentCancellationType.id ?? 0) > 0
                        ? 'Edit Job Assignment Cancellation Type'
                        : 'Add Job Assignment Cancellation Type'
                }
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onSave={this.onSave}
                onDelete={this.onDelete}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                readOnly={!canEdit}
                showDelete={showDelete}
            >
                <Input
                    label="Description"
                    id="description"
                    name="jobAssignmentCancellationType.description"
                    value={jobAssignmentCancellationType.description ?? ''}
                    onChange={this.onChange}
                    placeholder="Enter description"
                    error="Description is required."
                    required
                />
                {canEdit && (
                    <>
                        <FormGroup>
                            <FormCheckbox
                                id="jobAssignmentCancellationType.preventsReassignment"
                                name="jobAssignmentCancellationType.preventsReassignment"
                                checked={
                                    jobAssignmentCancellationType.preventsReassignment
                                    ?? ''
                                }
                                onChange={this.onChange}
                                labelText="Prevents Reassignment For The Rest Of The Day"
                            />
                        </FormGroup>
                        <FormGroup>
                            <FormCheckbox
                                id="jobAssignmentCancellationType.includeOnRosterView"
                                name="jobAssignmentCancellationType.includeOnRosterView"
                                checked={
                                    jobAssignmentCancellationType.includeOnRosterView
                                    ?? ''
                                }
                                onChange={this.onChange}
                                labelText="Include On Roster View"
                            />
                        </FormGroup>
                        {Boolean(
                            jobAssignmentCancellationType.includeOnRosterView
                            && !jobAssignmentCancellationType.preventsReassignment,
                        ) && (
                        <FormGroup>
                            <FormCheckbox
                                id="jobAssignmentCancellationType.treatAsUnassigned"
                                name="jobAssignmentCancellationType.treatAsUnassigned"
                                checked={
                                            jobAssignmentCancellationType.treatAsUnassigned
                                            ?? ''
                                        }
                                onChange={this.onChange}
                                labelText="Treat as Unassigned"
                            />
                        </FormGroup>
                            )}
                        <FormGroup>
                            <FormCheckbox
                                id="jobAssignmentCancellationType.showOnDispatchPlusHandoffEmail"
                                name="jobAssignmentCancellationType.showOnDispatchPlusHandoffEmail"
                                checked={
                                    jobAssignmentCancellationType.showOnDispatchPlusHandoffEmail
                                    ?? false
                                }
                                onChange={this.onChange}
                                labelText="Include on Dispatch+"
                            />
                        </FormGroup>
                        {jobAssignmentCancellationType.showOnDispatchPlusHandoffEmail && (
                            <Input
                                label="Dispatch+ Label"
                                id="dispatchPlusLabel"
                                name="jobAssignmentCancellationType.dispatchPlusLabel"
                                value={jobAssignmentCancellationType.dispatchPlusLabel ?? ''}
                                onChange={this.onChange}
                                placeholder="Optional..."
                                maxLength="250"
                            />
                        )}
                    </>
                )}

                {!canEdit && (
                    <>
                        <FormGroup>
                            <FormLabel label="Prevents Reassignment" />
                            <h3>
                                <Badge
                                    className="fifty-px"
                                    color={getBadgeColor(
                                        jobAssignmentCancellationType.preventsReassignment,
                                    )}
                                >
                                    {toYesNo(
                                        jobAssignmentCancellationType.preventsReassignment,
                                    )}
                                </Badge>
                            </h3>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel label="Include On Roster View" />
                            <h3>
                                <Badge
                                    className="fifty-px"
                                    color={getBadgeColor(
                                        jobAssignmentCancellationType.includeOnRosterView,
                                    )}
                                >
                                    {toYesNo(
                                        jobAssignmentCancellationType.includeOnRosterView,
                                    )}
                                </Badge>
                            </h3>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel label="Treat as Unassigned" />
                            <h3>
                                <Badge
                                    className="fifty-px"
                                    color={getBadgeColor(
                                        jobAssignmentCancellationType.treatAsUnassigned,
                                    )}
                                >
                                    {toYesNo(
                                        jobAssignmentCancellationType.treatAsUnassigned,
                                    )}
                                </Badge>
                            </h3>
                        </FormGroup>
                    </>
                )}

                <FormGroup>
                    <FormLabel label="Can Be Modified" />
                    <h3>
                        <Badge
                            className="fifty-px"
                            color={getBadgeColor(
                                jobAssignmentCancellationType.canBeModified,
                            )}
                        >
                            {toYesNo(
                                jobAssignmentCancellationType.canBeModified,
                            )}
                        </Badge>
                    </h3>
                </FormGroup>
                <FormGroup>
                    <FormLabel label="Can Be Deleted" />
                    <h3>
                        <Badge
                            className="fifty-px"
                            color={getBadgeColor(
                                jobAssignmentCancellationType.canBeDeleted,
                            )}
                        >
                            {toYesNo(
                                jobAssignmentCancellationType.canBeDeleted,
                            )}
                        </Badge>
                    </h3>
                </FormGroup>
                <FormGroup>
                    <FormLabel label="Is Deleted" />
                    <h3>
                        <Badge
                            className="fifty-px"
                            color={getBadgeColor(
                                jobAssignmentCancellationType.isDeleted,
                            )}
                        >
                            {toYesNo(
                                jobAssignmentCancellationType.isDeleted,
                            )}
                        </Badge>
                    </h3>
                </FormGroup>
            </SlideForm>
        );
    }
}
